/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useCurrentGlobalGroupId} from '@ion/applications/src/hooks/useCurrentGlobalGroupId';
import {useGridRowsPerPage} from '@ion/applications/src/hooks/useGridRowsPerPage';
import {useViewSubGroups} from '@ion/applications/src/hooks/useViewSubGroups';
import {dispatchGridColumnsChange} from '@ion/applications/src/store/helpers';
import {getBulkEntityDeleteMessage, getEntityDeleteMessage} from '@ion/applications/src/utils/translation';
import useHasMounted from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {
  AccountLink,
  AuditChange,
  CRUDPermissions,
  Entity,
  Feature,
  GenericObject,
  Group,
  GroupsFilters
} from '@ion/core/src/types/entities';
import {DownloadTemplate} from '@ion/core/src/types/templates';
import {DropDownDefaultLevelProps, DropDownInfiniteScrollProps} from '@ion/dls/src/components/menus/Dropdown';
import {AdminGroupDeletePrompt} from '@ion/dls/src/editors/AdminGroupDeletePrompt';
import {AuditDateFilterProps, AuditDialog} from '@ion/dls/src/editors/audit/AuditDialog';
import {ViewAuditChangesDialog} from '@ion/dls/src/editors/audit/ViewAuditChangesDialog';
import {BulkDeletePrompt} from '@ion/dls/src/editors/BulkDeletePrompt';
import {
  ModularSearchProps,
  PageViewSubGroupsProps,
  ViewSubGroupsPosition
} from '@ion/dls/src/types/types';
import {
  getViewExcludeInheritedGroupProps,
  getViewSubGroupsProps
} from '@ion/dls/src/utils/ComponentUtils';
import BaseSearchMapping from '@ion/features/src/search/BaseSearchMappings';
import {
  AttributeOperators,
  AttributeTypes,
  Mapping,
  SuggestionsType
} from '@ion/features/src/search/types';
import {ExportContext} from '@/types/redux';
import useExcludeInheritedGroup from '../../../hooks/useExcludeInheritedGroup';
import AdminContainer, {PageTitle} from '../AdminContainer';
import AddGroupEditor from './AddGroupEditor';
import EditGroupEditor from './EditGroupEditor';
import GroupsGrid, {GroupsColumns, StatusProps} from './GroupsGrid';
import {
  addGroup,
  deleteAdminGroup,
  deleteAdminGroups,
  deleteGroup,
  deleteGroups,
  groupAudit,
  initializeGroups,
  NAME,
  PREFERENCE,
  setGroupExcludeInheritedGroups,
  setGroupsGridPageSize,
  setGroupViewSubGroups,
  showAuditChangesDialog,
  UPDATE_GROUPS_GRID_COLUMNS,
  updateGroupsSearchQueries
} from './state';
import ViewGroupEditor from './ViewGroupEditor';

// [ION-APP TODO]: Use Hooks to refactor this operation of modular search.
// Reference: packages/ion-ctc-admin/src/routes/provisioning/devicefiles/DeviceFiles.tsx
// Defines the searchable attributes, options, and labels for modularized search.
const GroupsMappings = (): {[key: string]: Mapping} => ({
  name : {
    type       : AttributeTypes.TEXT,
    attributes : {
      value : AttributeOperators.CONTAINS,
      label : translate('common:modularsearch.attributes.contains')
    },
    suggestion : SuggestionsType.TEXT,
    label      : translate('groups:modularSearch.labels.name'),
    key        : 'name'
  }
});

// Defines the potential search types for modularized search.
const uniqueConditions = (): string =>
  (`(${translate('common:modularsearch.attributes.contains')})`);

export interface GroupsProps extends StatusProps,
  DropDownDefaultLevelProps,
  DropDownInfiniteScrollProps,
  AuditDateFilterProps,
  PageViewSubGroupsProps,
  ModularSearchProps
{
  onSelectAccount?: (value: AccountLink) => void;
  accountHierarchy?: AccountLink[];
  Features: Feature;
  isCtcAdmin: boolean;
  usePreferedPageSize?: boolean;
  includeGlobalSearch?: boolean;
  searchKeyword?: string;
  enableSelectionsExport?: boolean;
  filteredColumns?: GroupsColumns[];
  includeDeviceGridCheckboxes?: boolean;
  includeAssetGridCheckboxes?: boolean;
  refreshInitialization?: Function;
  groupsPermissions?: CRUDPermissions;
  hasInheritableGroups?: boolean;
  includeInheritedToggle?: boolean;
  ancestorsGroups?: GenericObject;
}

/**
 * Defines the Groups container component. This component is only to render
 * child components and provide top level subscriptions and dispatches to the
 * redux store.
 */
export const Groups = ({
  accountHierarchy,
  Features,
  onSelectAccount,
  enableSelectionsExport,
  filteredColumns,
  refreshInitialization,
  groupsPermissions,
  ancestorsGroups,
  viewSubGroupsPosition = ViewSubGroupsPosition.GRID_PAGINATION_CONTROLS,
  enableSubgroupToggle = false,
  searchKeyword = '',
  isCtcAdmin = false,
  includeGlobalSearch = false,
  showStatus = false,
  isChipClickable = false,
  includeInheritedToggle = false,
  hasInheritableGroups = false,
  enableDateFilter = false,
  includeDeviceGridCheckboxes = false,
  includeAssetGridCheckboxes = false,
  usePreferedPageSize = false,
  autoSelectNext = false,
  isInfiniteScroll = false
}: GroupsProps) => {
  const dispatch  = useDispatch();
  const isMounted = useHasMounted();

  const {
    result: deleteResult,
    selected,
    isEditing,
    loading,
    error
  } = useSelector(({groups: {destroy}}) => destroy);

  const {columnState}              = useSelector(({groups: {grid}}) => grid);
  const {isEditing: showAddGroup}  = useSelector(({groups: {add}}) => add);
  const {isEditing: showEditGroup} = useSelector(({groups: {edit}}) => edit);
  const {isEditing: showGroup}     = useSelector(({groups: {view}}) => view);
  const currentGlobalGroupId       = useCurrentGlobalGroupId();

  const {
    isEditing: showAuditGroup,
    selected: auditGroup
  } = useSelector(({groups: {audit}}) => audit);

  const {
    auditRecord,
    showChangesDialog
  } = useSelector(({groups: {audit}}) => audit);

  const {
    result: bulkDeleteResult,
    selected: bulkDeleteSelected,
    isEditing: bulkDeleteIsEditing,
    loading: bulkDeleteLoading,
    error: bulkDeleteError,
    record: bulkDeleteRecord
  } = useSelector(({groups: {bulkDelete}}) => bulkDelete);

  const gridRef                                           = useRef<GenericObject>();
  const [bulkDeleteErrorsState, setBulkDeleteErrorsState] = useState(null);

  const [viewSubGroups, toggleViewSubGroups] = useViewSubGroups({
    name       : NAME,
    action     : setGroupViewSubGroups,
    view       : enableSubgroupToggle,
    preference : PREFERENCE
  });

  const [
    viewExcludeInheritedGroup,
    toggleViewExcludeInheritedGroup
  ] = useExcludeInheritedGroup({
    name       : NAME,
    action     : setGroupExcludeInheritedGroups,
    preference : PREFERENCE
  });

  const [gridPageSize, setGridPageSize] = useGridRowsPerPage({
    name       : NAME,
    action     : setGroupsGridPageSize,
    preference : PREFERENCE
  });

  useEffect(() => {
    if (!isMounted) {
      dispatch(initializeGroups());
    }
  }, [isMounted]);

  useEffect(() => {
    setBulkDeleteErrorsState(bulkDeleteError);
  }, [bulkDeleteError]);

  /**
   * @private
   */
  const onExport = function(downloadParams?: DownloadTemplate) {
    gridRef.current?.onExport(downloadParams);
  };
  /**
   * @private
   */
  const onRefresh = function() {
    gridRef.current?.onRefresh(true);
  };

  /**
   * @private
   */
  function toggleAddGroupsEditor() {
    dispatch(addGroup());
  };

  /**
   * @private
   */
  function onDeleteCancel() {
    dispatch(deleteAdminGroup(selected, false));
  }

  /**
   * @private
   */
  const onDeleteConfirm = function(Groups: Group) {
    dispatch(deleteGroup(selected) as any);
  };

  const getDeleteMessage = function() {
    if (selected && !Array.isArray(selected)) {
      return getEntityDeleteMessage(selected);
    }
    if (!selected && bulkDeleteSelected) {
      return getBulkEntityDeleteMessage(Entity.group, bulkDeleteSelected);
    }
  };

  function onViewAuditChanges(auditRecord: AuditChange, show = false) {
    dispatch(showAuditChangesDialog(auditRecord, show));
  };

  function onAuditClose() {
    dispatch(groupAudit(null, false));
  }

  function onViewAuditChangesClose() {
    dispatch(showAuditChangesDialog(null, false));
  };

  const onBulkDeleteConfirm = function() {
    dispatch(deleteGroups(bulkDeleteSelected) as any);
  };

  function onBulkDeleteCancel() {
    dispatch(deleteAdminGroups(bulkDeleteSelected, false));
    setBulkDeleteErrorsState(null);
  }

  /**
   * @private
   */
  const onUpdateGridColumns = function() {
    dispatch(dispatchGridColumnsChange(columnState, NAME, UPDATE_GROUPS_GRID_COLUMNS) as any);
  };

  // Dispatches the given search params to the search redux state for modularized search.
  const getSearchParams = useCallback(function(filters: GroupsFilters) {
    dispatch(updateGroupsSearchQueries(filters));
    onRefresh();
  }, []);

  const mappings = useMemo(() => {
    return new BaseSearchMapping(GroupsMappings(), uniqueConditions(), currentGlobalGroupId);
  }, [currentGlobalGroupId]);

  const viewSubGroupsProps = useMemo(() => getViewSubGroupsProps(
    viewSubGroupsPosition,
    viewSubGroups as boolean,
    toggleViewSubGroups as Function
  ), [viewSubGroupsPosition, viewSubGroups, toggleViewSubGroups]);

  const viewExcludeInheritedGroupProps = useMemo(() => getViewExcludeInheritedGroupProps(
    viewSubGroupsPosition,
    viewExcludeInheritedGroup as boolean,
    toggleViewExcludeInheritedGroup as Function
  ), [
    viewSubGroupsPosition,
    viewExcludeInheritedGroup,
    toggleViewExcludeInheritedGroup
  ]);

  return (
    <>
      <AdminContainer includeExport
        isCtcAdmin={isCtcAdmin}
        autoSelectNext={autoSelectNext}
        grid={
          <GroupsGrid ref={gridRef}
            isCtcAdmin={isCtcAdmin}
            usePreferedPageSize={usePreferedPageSize}
            gridPageSize={gridPageSize}
            setGridPageSize={setGridPageSize}
            showStatus={showStatus}
            Features={Features}
            includeGlobalSearch={includeGlobalSearch}
            searchKeyword={searchKeyword}
            ancestorsGroups={ancestorsGroups}
            hasInheritableGroups={hasInheritableGroups}
            groupsPermissions={groupsPermissions}
            enableSelectionsExport={enableSelectionsExport}
            onSelectAccount={onSelectAccount}
            refreshInitialization={refreshInitialization}
            accountHierarchy={accountHierarchy}
            filteredColumns={filteredColumns}
            {...viewSubGroupsProps[ViewSubGroupsPosition.GRID_PAGINATION_CONTROLS]}
            {...viewExcludeInheritedGroupProps[ViewSubGroupsPosition.GRID_PAGINATION_CONTROLS]} />
        }
        getSearchParams={getSearchParams}
        searchInstance={mappings}
        searchLabel={translate('groups:search')}
        reducer={NAME}
        title={PageTitle.adminGroups}
        onAdd={toggleAddGroupsEditor}
        exportContext={ExportContext.groups}
        onExport={onExport}
        onRefresh={onRefresh}
        onUpdateGridColumns={onUpdateGridColumns}
        includeInheritedToggle={includeInheritedToggle}
        onShowColumnChooser={gridRef.current?.toggleColumnChooser}
        includeGlobalSearch={includeGlobalSearch}
        {...viewSubGroupsProps[ViewSubGroupsPosition.GRID_CTAS]}
        {...viewExcludeInheritedGroupProps[ViewSubGroupsPosition.GRID_CTAS]} />
      {showAddGroup && (
        <AddGroupEditor isCtcAdmin={isCtcAdmin}
          hasInheritableGroups={hasInheritableGroups}
          includeDeviceGridCheckboxes={includeDeviceGridCheckboxes}
          includeAssetGridCheckboxes={includeAssetGridCheckboxes}
          isInfiniteScroll={isInfiniteScroll} />
      )}
      {showEditGroup && (
        <EditGroupEditor isCtcAdmin={isCtcAdmin}
          isChipClickable={isChipClickable}
          hasInheritableGroups={hasInheritableGroups}
          includeDeviceGridCheckboxes={includeDeviceGridCheckboxes}
          includeAssetGridCheckboxes={includeAssetGridCheckboxes}
          isInfiniteScroll={isInfiniteScroll} />
      )}
      {showGroup && (
        <ViewGroupEditor isChipClickable={isChipClickable}
          includeDeviceGridCheckboxes={includeDeviceGridCheckboxes}
          hasInheritableGroups={hasInheritableGroups}
          ancestorsGroups={ancestorsGroups}
          groupsPermissions={groupsPermissions}
          includeAssetGridCheckboxes={includeAssetGridCheckboxes} />
      )}
      {showAuditGroup && (
        <AuditDialog enableDateFilter={enableDateFilter}
          rowMenusEnabled
          showDifferences
          item={auditGroup}
          entity={Entity.group}
          onCloseDialog={onAuditClose}
          onViewAuditChanges={onViewAuditChanges} />
      )}
      {(showChangesDialog && auditRecord) && (
        <ViewAuditChangesDialog auditRecord={auditRecord}
          onCloseDialog={onViewAuditChangesClose}
          entity={Entity.group} />
      )}
      <AdminGroupDeletePrompt show={isEditing}
        onDeleteConfirm={onDeleteConfirm}
        onDeleteCancel={onDeleteCancel}
        overrideMessage={getDeleteMessage}
        group={selected}
        result={deleteResult}
        loading={loading}
        error={error} />
      {(bulkDeleteIsEditing || bulkDeleteRecord) &&
        (<BulkDeletePrompt show={bulkDeleteIsEditing}
          items={bulkDeleteSelected}
          entity={translate('common:entityLabels.groups')}
          result={bulkDeleteResult}
          loading={bulkDeleteLoading}
          error={bulkDeleteErrorsState}
          overrideMessage={getDeleteMessage}
          deletedItems={bulkDeleteRecord}
          onDeleteConfirm={onBulkDeleteConfirm}
          onDeleteCancel={onBulkDeleteCancel} />)}
    </>
  );
};
export default Groups;
